import { ChangeDetectionStrategy, Component, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

@Component({
  selector: 'ds-toggle',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FieldErrorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true,
    },
  ],
  template: `
    <div class="ds-field">
      <label
        class="toggle"
        [class.toggle--checked]="value()"
        [class.toggle--disabled]="disabled()"
      >
        <input
          class="toggle__input"
          type="checkbox"
          role="switch"
          [id]="fieldId"
          [checked]="value() ?? false"
          [disabled]="disabled()"
          [required]="required()"
          [attr.aria-invalid]="errorText() ? 'true' : null"
          [attr.aria-describedby]="describedBy()"
          (change)="toggle($event)"
          (blur)="onTouched()"
        />
        <span class="toggle__track" aria-hidden="true">
          <span class="toggle__thumb"></span>
        </span>
        <span class="toggle__text">
          <span class="toggle__label">{{ label() }}</span>
          @if (helperText()) {
            <span class="ds-field__helper toggle__helper" [id]="fieldId + '-helper'">
              {{ helperText() }}
            </span>
          }
        </span>
      </label>
      @if (errorText()) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ errorText() }}</ds-field-error>
      }
    </div>
  `,
  styles: `
    .toggle {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-1) 0;
      cursor: pointer;

      &--disabled {
        cursor: not-allowed;

        .toggle__label,
        .toggle__helper {
          color: var(--color-text-disabled);
        }
      }
    }

    .toggle__input {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      clip-path: inset(50%);
      overflow: hidden;
    }

    .toggle__track {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      width: 40px;
      height: 22px;
      padding: 2px;
      border-radius: var(--radius-full);
      background: var(--color-border-input);
      transition: background-color var(--motion-fast);
    }

    .toggle__thumb {
      display: block;
      width: 18px;
      height: 18px;
      border-radius: var(--radius-full);
      background: var(--color-surface);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      transition: transform var(--motion-fast);
    }

    .toggle--checked .toggle__track {
      background: var(--color-primary);
    }

    .toggle--checked .toggle__thumb {
      transform: translateX(18px);
    }

    .toggle--disabled .toggle__track {
      background: var(--color-surface-sunken);
    }

    .toggle--disabled.toggle--checked .toggle__track {
      background: var(--color-primary-tint-strong);
    }

    .toggle__input:focus-visible + .toggle__track {
      outline: var(--focus-ring);
      outline-offset: var(--focus-offset);
    }

    .toggle__text {
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .toggle__label {
      font-size: var(--text-sm);
      color: var(--color-text);
    }

    .toggle__helper {
      margin: 0;
    }
  `,
})
export class ToggleComponent extends FieldBase<boolean> {
  protected toggle(event: Event): void {
    const next = (event.target as HTMLInputElement).checked;
    this.value.set(next);
    this.onChange(next);
  }
}
